// Generic paginated reader. PostgREST caps a single response at ~1000 rows, so
// any aggregate over a whole table/tenant MUST page or it silently under-reports
// on large tenants (see CLAUDE.md convention #11).
//
// Pass a builder that applies your filters, a STABLE order on a unique column
// (use the primary key `id` — ordering by a non-unique column lets Postgres split
// ties across page boundaries and skip/duplicate rows), and `.range(from, to)`.
// The loop stops on the first short page.
//
//   const rows = await fetchAllPaged<Row>((from, to) =>
//     db('sales_orders').select('id, total').eq('tenant_id', tid)
//       .order('id', { ascending: true }).range(from, to))

const PAGE = 1000

export async function fetchAllPaged<T>(
  build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  const out: T[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await build(from, from + PAGE - 1)
    if (error) throw error
    const rows = data ?? []
    out.push(...rows)
    if (rows.length < PAGE) break
  }
  return out
}

// Fetch rows for a large list of IDs without blowing the URL length limit. A
// single `.in('col', ids)` encodes every id into the query string; past a few
// hundred ids the URL exceeds proxy limits and the request 414s or silently
// truncates, dropping enrichment rows. This runs the builder over ID chunks and
// concatenates. Use when an embed join isn't possible (e.g. a polymorphic
// source_id with no FK).
//
//   const rows = await fetchByIdsChunked(ids, (batch) =>
//     db('sales_orders').select('id, order_number').eq('tenant_id', tid).in('id', batch))

const ID_CHUNK = 200

export async function fetchByIdsChunked<T>(
  ids: string[],
  build: (batch: string[]) => PromiseLike<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  const out: T[] = []
  for (let i = 0; i < ids.length; i += ID_CHUNK) {
    const { data, error } = await build(ids.slice(i, i + ID_CHUNK))
    if (error) throw error
    out.push(...(data ?? []))
  }
  return out
}
